import * as Sentry from '@sentry/react';
import {
  FieldPasswordCompact,
  FieldTextCompact,
  Pushbutton
} from '@stratumn/atomic';
import to from 'await-to-js';
import { Box, Divider, Logo, OauthOptions } from 'components/ui';
import { env } from 'constant/env';
import { ROUTE_FORGOT } from 'constant/routes';
import {
  AUTH_PROVIDER_CONDUENT,
  AUTH_PROVIDER_MICROSOFT_SIA,
  AUTH_PROVIDER_ONE_ACCOUNT_AXA
} from 'constant/types';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import qs from 'query-string';
import React from 'react';
import injectSheet from 'react-jss';
import { Link, withRouter } from 'react-router-dom';
import { SpanType, withSpanAsync } from 'tracing';
import { redirectAfterLogin } from 'utils/loginRedirect';
import { withRestClient } from 'wrappers';

import styles from './login.style';

const loginProviders = () => {
  if (env.VITE_ENVIRONMENT === 'staging') {
    return null;
  }
  if (env.VITE_IS_US_REGION) {
    return [AUTH_PROVIDER_MICROSOFT_SIA, AUTH_PROVIDER_CONDUENT];
  }
  return [AUTH_PROVIDER_ONE_ACCOUNT_AXA, AUTH_PROVIDER_MICROSOFT_SIA];
};

export class Login extends React.PureComponent {
  static propTypes = {
    location: PropType.object.isRequired,
    history: PropType.object.isRequired,
    classes: PropType.object.isRequired,
    client: PropType.object.isRequired
  };

  state = {
    email: '',
    password: '',
    showPassword: false,
    loading: false
  };

  togglePassword = () =>
    this.setState({ showPassword: !this.state.showPassword });

  handleChange = name => event => {
    this.setState({ [name]: event.target.value });
  };

  isValid() {
    const { email, password } = this.state;
    return !!email && !!password;
  }

  login = async event =>
    withSpanAsync('login', SpanType.processing, async () => {
      if (event) event.preventDefault();
      if (this.state.loading) return;

      this.setState({ loading: true });

      const { location, history } = this.props;
      const { email, password } = this.state;

      const {
        responseType,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod
      } = qs.parse(location.search);

      const [err, rsp] = await to(
        this.props.client.login(email, password, {
          responseType,
          clientId,
          redirectUri,
          codeChallenge,
          codeChallengeMethod
        })
      );

      this.setState({ loading: false });

      if (err) {
        Sentry.captureException(err);
        return;
      }

      const { redirectUri: r, authCode } = rsp;
      redirectAfterLogin(history, r, authCode);
    });

  render() {
    const { classes } = this.props;
    const { showPassword, email, password, loading } = this.state;

    const emailInputProps = {
      label: 'Email',
      onValueChange: this.handleChange('email'),
      value: email
    };

    const passwordInputProps = {
      label: 'Password',
      onValueChange: this.handleChange('password'),
      value: password,
      onVisibilityChange: this.togglePassword,
      passwordIsVisible: showPassword
    };

    return (
      <>
        <Logo />
        <Box title="Log in">
          <form
            className={classes.form}
            onSubmit={this.isValid() ? this.login : undefined}
          >
            <div className={classes.formField}>
              <FieldTextCompact {...emailInputProps} data-testid="email" />
            </div>
            <div className={classes.formField}>
              <FieldPasswordCompact
                {...passwordInputProps}
                data-testid="password"
              />
            </div>
            <div className={classes.forgot} data-testid="forgot-password">
              <Link to={ROUTE_FORGOT}>Forgot your password?</Link>
            </div>
            <div className={classes.optionBox}>
              <Pushbutton
                data-testid="login-button"
                disabled={!this.isValid() || loading}
                primary
                type="submit"
              >
                {loading ? (
                  <span className={classes.spinner} data-testid="loader" />
                ) : (
                  'Log in'
                )}
              </Pushbutton>
            </div>
          </form>
          <Divider text="Or log in with" />
          <OauthOptions providers={loginProviders()} />
          <div className={classes.signUp}>
            <b>Don&apos;t have an account?</b> Currently Stratumn ID is only
            available to clients using our product range. If you would like to
            learn more, <a href="mailto:hello@stratumn.com">contact us</a>.
          </div>
        </Box>
      </>
    );
  }
}

export default compose(withRouter, withRestClient, injectSheet(styles))(Login);
